import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import { pipeNetwork } from '../../../packages/domain/src/57-score-puzzles.mjs';
import { MERGE_MILESTONES } from '../../../packages/domain/src/56-score-board-games.mjs';
import { paintIconAction } from './82-icon-actions.mjs';

const profiles = {
  'soft-merge': {
    emblem: 0,
    icon: 'move-horizontal',
    quest: 'MERGE STREAK',
    steps: [
      [
        'Make your first fusion',
        'Swipe the board, use arrow keys, or tap an arrow below. Equal tiles combine once per move.',
        ['2', '2', '→', '4'],
        'Slide to merge',
        ['4', '4', '→', '8'],
      ],
      [
        'Keep the fusion going',
        'Consecutive moves that merge build a streak up to five. A move without a merge resets it.',
        ['4', '4', '8', '8'],
        'Build a streak',
        ['8', '+', '16', '✦'],
      ],
      [
        'Reach the next prism',
        'Keep room for new tiles. Milestones earn extra points; your final target is 8192 on a 6 × 6 board.',
        ['128', '512', '2048', '8192'],
        'Light the prisms',
        ['✦', '✦', '✦', '8192'],
      ],
    ],
  },
  'block-fit': {
    emblem: 1,
    icon: 'grid-2x2',
    quest: 'CLEAR STREAK',
    steps: [
      [
        'Pick, turn, place',
        'Choose one of three pieces. Rotate it, then tap a glowing fit or drag it onto the board.',
        ['■', '■', '□', '□'],
        'Place the piece',
        ['■', '■', '■', '■'],
      ],
      [
        'Set up a cross clear',
        'Full rows and columns clear together. Completing several in one placement earns an extra bonus.',
        ['■', '■', '□', '■'],
        'Clear two lines',
        ['✦', '✦', '✦', '✦'],
      ],
      [
        'Keep the board breathing',
        'Clears on consecutive placements build a streak. Empty the whole board for an all-clear bonus.',
        ['■', '■', '■', '□'],
        'Make an all-clear',
        ['✦', '□', '□', '✦'],
      ],
    ],
  },
  'colour-path': {
    emblem: 2,
    icon: 'move-horizontal',
    quest: 'COVER EVERY SQUARE',
    steps: [
      [
        'Follow a matching symbol',
        'Start at an endpoint. Drag through neighbouring squares or tap them one at a time to reach its match.',
        ['●', '·', '·', '●'],
        'Connect the ribbon',
        ['●', '━', '━', '●'],
      ],
      [
        'Leave room for every ribbon',
        'Lines cannot cross. Match every pair and cover all 16 squares; connected pairs alone are not enough.',
        ['●', '◆', '·', '◆'],
        'Fill the gap',
        ['●', '◆', '━', '◆'],
      ],
      [
        'Redraw without a penalty',
        'Trace back along your current line to shorten it. Use Redraw current line to start that ribbon again.',
        ['●', '━', '┓', '·'],
        'Trace back',
        ['●', '━', '·', '·'],
      ],
    ],
  },
  'memory-pairs': {
    emblem: 3,
    icon: 'eye-off',
    quest: 'MATCH STREAK',
    steps: [
      [
        'Meet the hidden friends',
        'Turn over two cards. Remember both the character and its position.',
        ['?', '?', '?', '?'],
        'Turn two cards',
        ['FOX', 'FROG', '?', '?'],
      ],
      [
        'A miss is useful information',
        'Different friends stay visible briefly, then turn back over in the same positions. Your found pairs stay safe.',
        ['FOX', 'FROG', '?', '?'],
        'Remember their places',
        ['?', '?', '?', '?'],
      ],
      [
        'Bring friends together',
        'Match pairs on consecutive attempts to build a streak. The next garden adds more cards.',
        ['FOX', '?', 'FOX', '?'],
        'Match the foxes',
        ['FOX', '✦', 'FOX', '✦'],
      ],
    ],
  },
  'tile-slide': {
    emblem: 4,
    icon: 'grid-2x2',
    quest: 'RESTORE THE PARADE',
    steps: [
      [
        'The empty space is your tool',
        'Tap a tile beside the gap, or drag it into the gap. Only a horizontal or vertical neighbour can move.',
        ['1', '2', '□', '3'],
        'Slide into the gap',
        ['1', '2', '3', '□'],
      ],
      [
        'Use the picture and numbers',
        'Restore 1–8 in reading order with the gap at bottom right. The small target below is always available.',
        ['1', '3', '2', '□'],
        'See the target',
        ['1', '2', '3', '□'],
      ],
      [
        'Plan a short sequence',
        'Work on the top row, then the remaining tiles. Each solved picture unlocks a more scrambled parade.',
        ['1', '2', '3', '→'],
        'Plan the next row',
        ['4', '5', '6', '→'],
      ],
    ],
  },
  'rotation-loop': {
    emblem: 5,
    icon: 'rotate-cw',
    quest: 'LIGHT THE NETWORK',
    steps: [
      [
        'Turn a lantern connection',
        'Tap a pipe to rotate it clockwise. Connect the source to its neighbours to spread the light.',
        ['┗', '┏', '┓', '┛'],
        'Rotate clockwise',
        ['┏', '┓', '┛', '┗'],
      ],
      [
        'Follow the light',
        'A connection lights only when both pipe ends meet. Work outward from the source.',
        ['●', '━', '┓', '·'],
        'Join the connection',
        ['●', '━', '━', '✦'],
      ],
      [
        'Seal every open end',
        'All nine pipes must light, including the destination, with no leaking ends. The leak counter shows what remains.',
        ['✦', '9/9', '2', '↻'],
        'Seal the network',
        ['✦', '9/9', '0', '✓'],
      ],
    ],
  },
  'number-trail': {
    emblem: 6,
    icon: 'hash',
    quest: 'CLEAN NUMBER CHAIN',
    steps: [
      [
        'Find the next firefly',
        'Tap the numbers in order, starting at 1. The NEXT display shows your immediate goal.',
        ['3', '1', '4', '2'],
        'Find 1',
        ['3', '✓', '4', '2'],
      ],
      [
        'Look one number ahead',
        'A clean sequence builds a points bonus. Find the next few numbers with your eyes before tapping.',
        ['1', '2', '3', '4'],
        'Follow 1 → 2 → 3',
        ['✓', '✓', '✓', '4'],
      ],
      [
        'Keep your trail',
        'A wrong number breaks the chain, but keeps every number you already found. Finish three growing meadows.',
        ['✓', '✓', '5', '3'],
        'Continue at 3',
        ['✓', '✓', '5', '✓'],
      ],
    ],
  },
  'spot-pair': {
    emblem: 7,
    icon: 'images',
    quest: 'PERFECT PAIR STREAK',
    steps: [
      [
        'Find the twin trinkets',
        'Exactly two objects share both shape and colour. Tap one, then its identical twin.',
        ['★', '◆', '●', '★'],
        'Choose the twins',
        ['★', '·', '·', '★'],
      ],
      [
        'Compare both details',
        'The same shape in a different colour is not a match. Selected trinkets lift so you can compare them.',
        ['◆', '◇', '●', '◆'],
        'Match shape and colour',
        ['◆', '·', '·', '◆'],
      ],
      [
        'Build a clean collection',
        'Consecutive correct pairs build a bonus up to five. Ten displays get busier; a miss keeps your earned score.',
        ['1', '2', '3', '4'],
        'Collect a clean streak',
        ['★', '★', '★', '★'],
      ],
    ],
  },
};

/** The score cabinet reads canonical state; these metrics never award points,
 * reveal hidden cards or provide a generated solution. Unsupported games return null. */
export function scoreArcadeMetrics(gameId, state, view) {
  const cells = view.cells || [],
    combo = view.combo || 0;
  const selected = cells.filter((cell) => cell.selected).length;
  switch (gameId) {
    case 'soft-merge': {
      const largest = Math.max(0, ...cells.map((cell) => cell.value || 0));
      const finalTarget = view.progress?.total || 8192;
      const target = Math.min(
        finalTarget,
        MERGE_MILESTONES.find((value) => value > largest) || finalTarget,
      );
      return {
        value: String(largest),
        label: 'largest prism',
        detail: `NEXT ${target} · ${cells.filter((cell) => !cell.value).length} spaces`,
        current: largest,
        total: target,
        combo,
      };
    }
    case 'block-fit':
      return {
        value: String(Math.max(0, (view.progress?.total || 30) - (view.progress?.current || 0))),
        label: 'pieces left',
        detail: 'PLAN ALL 3 PIECES · CROSS CLEAR FOR MORE',
        current: view.progress?.current || 0,
        total: view.progress?.total || 30,
        combo,
      };
    case 'colour-path': {
      const endpoints = cells.filter((cell) => cell.endpoint && cell.selected).length;
      return {
        value: `${selected}/16`,
        label: 'squares linked',
        detail: `${Math.floor(endpoints / 2)} PAIRS LINKED · FILL EVERY SQUARE`,
        current: selected,
        total: 16,
        combo: 0,
      };
    }
    case 'memory-pairs': {
      const matched = cells.filter((cell) => cell.matched).length / 2;
      const opened = cells.filter((cell) => cell.value !== null && !cell.matched).length;
      return {
        value: `${matched}/${cells.length / 2}`,
        label: 'friends paired',
        detail:
          opened === 2
            ? 'REMEMBER THESE POSITIONS'
            : opened === 1
              ? 'FIND THIS FRIEND’S TWIN'
              : 'TURN TWO CARDS · KEEP A CLEAN STREAK',
        current: matched,
        total: cells.length / 2,
        combo,
      };
    }
    case 'tile-slide': {
      const correct = cells.filter(
        (cell, index) => cell.value !== 0 && cell.value === index + 1,
      ).length;
      return {
        value: `${correct}/8`,
        label: 'tiles in place',
        detail: 'READ LEFT TO RIGHT · GAP AT BOTTOM RIGHT',
        current: correct,
        total: 8,
        combo: 0,
      };
    }
    case 'rotation-loop': {
      const network = pipeNetwork(state.data);
      return {
        value: `${network.lit.length}/9`,
        label: 'lanterns lit',
        detail: `${network.leaks} OPEN ${network.leaks === 1 ? 'END' : 'ENDS'} · LIGHT ALL 9, SEAL EVERY END`,
        current: network.lit.length,
        total: 9,
        combo: 0,
      };
    }
    case 'number-trail': {
      const next = selected + 1;
      return {
        value: selected === cells.length ? '✓' : String(next),
        label: 'next firefly',
        detail:
          selected === cells.length
            ? 'MEADOW COMPLETE'
            : `NEXT ${Array.from({ length: Math.min(3, cells.length - selected) }, (_, index) => next + index).join(' → ')}`,
        current: selected,
        total: cells.length,
        combo,
      };
    }
    case 'spot-pair':
      return {
        value: `${view.progress?.current || 0}/${view.progress?.total || 10}`,
        label: state.policy ? 'pair this board' : 'pairs collected',
        detail: selected ? 'FIND THE SAME SHAPE + COLOUR' : 'ONE IDENTICAL PAIR · SHAPE + COLOUR',
        current: view.progress?.current || 0,
        total: view.progress?.total || 10,
        combo,
      };
    default:
      return null;
  }
}

/** Replace text-heavy cabinet labels with persistent native icon/value chips.
 * Full metric names remain available to assistive technology and pointer tooltips. */
export function createScoreArcadeHud(ctx, gameId, arcade, level, score) {
  if (!profiles[gameId]) return null;
  const objective = ctx.e('span', 'score-objective-chip');
  arcade.append(objective);
  function chip(node, icon, value, label) {
    node.title = label;
    node.setAttribute('aria-label', label);
    node.replaceChildren(ctx.e('strong', '', String(value)));
    if (ctx.icon) node.insertBefore(ctx.icon(icon), node.firstChild);
  }
  return (state, view, best) => {
    const metric = scoreArcadeMetrics(gameId, state, view);
    chip(level, 'flag', view.stage || 1, `Level ${view.stage || 1}`);
    chip(
      score,
      'star',
      state.score.toLocaleString(),
      `${state.score} points · Session best ${best}`,
    );
    chip(objective, profiles[gameId].icon, metric.value, `${metric.label}: ${metric.value}`);
  };
}

function emblem(ctx, profile, className = '') {
  const node = ctx.e('span', `score-arcade-emblem ${className}`);
  node.setAttribute('aria-hidden', 'true');
  node.style.setProperty('--emblem-x', `${((profile.emblem % 4) * 100) / 3}%`);
  node.style.setProperty('--emblem-y', `${Math.floor(profile.emblem / 4) * 100}%`);
  return node;
}

/** Per-game public goals and earned sequence cues reuse the existing board,
 * moves and clock. All effects are finite and cancelled on suspension/disposal. */
export function createScoreArcadePresentation(
  ctx,
  { game, host, board, playfield, arcade, readState, enabled },
) {
  const profile = profiles[game.id];
  if (!profile) return null;
  host.dataset.scoreArcade = 'true';
  const panel = ctx.e('section', 'score-arcade-dashboard');
  panel.setAttribute('aria-label', `${game.title} objective`);
  const art = emblem(ctx, profile),
    copy = ctx.e('div', 'score-arcade-copy'),
    goal = ctx.e('strong', 'score-arcade-goal'),
    detail = ctx.e('span', 'score-arcade-detail'),
    track = ctx.e('span', 'score-arcade-track'),
    fill = ctx.e('i'),
    chain = ctx.e('div', 'score-arcade-chain'),
    chainLabel = ctx.e('span', '', profile.quest),
    chainValue = ctx.e('strong'),
    links = Array.from({ length: 5 }, () => ctx.e('i'));
  track.setAttribute('aria-hidden', 'true');
  track.append(fill);
  copy.append(goal, detail, track);
  chain.append(chainLabel, ...links, chainValue);
  panel.append(art, copy, chain);
  host.insertBefore(panel, arcade.nextSibling);
  if (game.id === 'tile-slide') {
    const target = ctx.e('div', 'score-slide-target');
    target.setAttribute('aria-label', 'Target: 1 2 3, 4 5 6, 7 8 empty');
    target.title = 'Restore this order';
    for (let index = 1; index <= 9; index++)
      target.append(ctx.e('span', '', index === 9 ? '·' : String(index)));
    panel.append(target);
  }
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  let disposed = false;
  const effects = new Set();
  function cancel() {
    for (const effect of effects) effect.cancel();
    effects.clear();
  }
  function animate(node, frames, options) {
    if (media.matches || typeof node.animate !== 'function') return;
    const motion = node.animate(frames, options);
    effects.add(motion);
    void motion.finished?.then(
      () => effects.delete(motion),
      () => effects.delete(motion),
    );
  }
  function reward(label, indexes, global = false) {
    if (!label || !enabled() || document.hidden || disposed) return;
    const source = indexes
      .map((index) => board.querySelector?.(`[data-cell="${index}"]`)?.getBoundingClientRect?.())
      .filter((rect) => rect?.width && rect.height);
    if (!global && !source.length) return;
    const toast = ctx.e('div', 'score-arcade-award');
    toast.setAttribute('aria-hidden', 'true');
    toast.append(emblem(ctx, profile), ctx.e('strong', '', label));
    const parent = playfield.getBoundingClientRect?.();
    if (!global && parent?.width && parent.height) {
      const x =
        source.reduce((sum, rect) => sum + rect.left + rect.width / 2, 0) / source.length -
        parent.left;
      const y =
        source.reduce((sum, rect) => sum + rect.top + rect.height / 2, 0) / source.length -
        parent.top;
      toast.style.left = `${Math.max(64, Math.min(parent.width - 64, x))}px`;
      toast.style.top = `${Math.max(18, Math.min(parent.height - 50, y - 24))}px`;
    }
    playfield.append(toast);
    let timer;
    const resource = {
      cancel() {
        globalThis.clearTimeout(timer);
        toast.remove();
        effects.delete(resource);
      },
    };
    effects.add(resource);
    animate(
      toast,
      [
        { transform: 'translate(-50%, 6px) scale(.85)', opacity: 0 },
        { transform: 'translate(-50%, 0) scale(1)', opacity: 1, offset: 0.2 },
        { transform: 'translate(-50%, -10px) scale(1)', opacity: 1, offset: 0.75 },
        { transform: 'translate(-50%, -16px)', opacity: 0 },
      ],
      { duration: 1050, easing: 'ease-out', fill: 'forwards' },
    );
    timer = globalThis.setTimeout(resource.cancel, 1100);
  }
  function preference() {
    if (media.matches) cancel();
  }
  media.addEventListener?.('change', preference);
  return {
    update(view) {
      if (disposed) return;
      const metric = scoreArcadeMetrics(game.id, readState(), view);
      goal.textContent = `${metric.value} ${metric.label}`;
      detail.textContent = metric.detail;
      fill.style.transform = `scaleX(${Math.max(0, Math.min(1, metric.current / (metric.total || 1)))})`;
      const hasChain = !['colour-path', 'tile-slide', 'rotation-loop'].includes(game.id);
      chain.hidden = !hasChain;
      chain.setAttribute('aria-label', `${profile.quest}: ${metric.combo}`);
      chainValue.textContent = metric.combo > 0 ? String(metric.combo) : '—';
      links.forEach((link, index) => {
        link.dataset.lit = String(index < metric.combo);
      });
    },
    accepted(previous, state) {
      cancel();
      if (disposed || state === previous || !enabled()) return;
      const view = gameView(state),
        events = view.events || [];
      const stage = state.completed || view.stageComplete;
      const indices = events.flatMap((event) => event.cells || []);
      if (stage)
        reward(
          ['stalled', 'move-limit'].includes(view.completionReason)
            ? 'BOARD FINISHED'
            : state.completed
              ? 'CHALLENGE COMPLETE'
              : `LEVEL ${view.stage} CLEAR`,
          [],
          true,
        );
      else if (events.some((event) => event.type === 'all-clear')) reward('ALL CLEAR!', indices);
      else if (events.some((event) => event.type === 'milestone'))
        reward(`${events.findLast((event) => event.type === 'milestone').target} PRISM!`, indices);
      else if (game.id === 'colour-path' && events.some((event) => event.type === 'connect'))
        reward('RIBBON LINKED', indices);
      else if (game.id === 'block-fit' && events.some((event) => event.lines > 1))
        reward('CROSS CLEAR!', indices);
      else if (view.combo >= 3 && state.score > previous.score)
        reward(
          `${view.combo} ${game.id === 'number-trail' ? 'CLEAN FINDS' : game.id === 'memory-pairs' ? 'PAIR STREAK' : 'CHAIN'}`,
          indices,
        );
      if (state.score > previous.score)
        animate(
          art,
          [
            { transform: 'scale(.9) rotate(-5deg)' },
            { transform: 'scale(1.12) rotate(5deg)', offset: 0.45 },
            { transform: 'scale(1) rotate(0)' },
          ],
          { duration: 420, easing: 'ease-out' },
        );
    },
    cancel,
    dispose() {
      disposed = true;
      cancel();
      media.removeEventListener?.('change', preference);
      panel.remove();
    },
  };
}

/** A skippable three-step rehearsal teaches each game's actual interaction and
 * sequence. Practice is isolated from the live board, score, RNG and transcript. */
export function createScoreArcadeGuide(ctx, { game, playfield, board, help, auto = false }) {
  const profile = profiles[game.id];
  if (!profile) return null;
  const panel = ctx.e('section', 'score-arcade-guide');
  panel.hidden = true;
  panel.setAttribute('role', 'region');
  panel.setAttribute('aria-label', `${game.title} introduction`);
  const heading = ctx.e('div', 'score-guide-heading'),
    title = ctx.e('strong', 'score-guide-title'),
    count = ctx.e('span', 'score-guide-count'),
    caption = ctx.e('p', 'score-guide-caption'),
    demo = ctx.e('div', 'score-guide-demo'),
    result = ctx.e('p', 'score-guide-result'),
    actions = ctx.e('div', 'score-guide-actions'),
    skip = ctx.e('button', 'game-action'),
    next = ctx.e('button', 'game-action');
  result.setAttribute('role', 'status');
  skip.type = next.type = 'button';
  paintIconAction(ctx, skip, 'x', 'Skip introduction', 'Skip');
  heading.append(emblem(ctx, profile), title, count);
  actions.append(skip, next);
  panel.append(heading, caption, demo, result, actions);
  playfield.append(panel);
  let step = 0,
    disposed = false,
    practiced = false;
  const storageKey = `myaudience-score-guide-${game.id}-v1`;
  function paintDemo(values) {
    demo.replaceChildren();
    const strip = ctx.e('div', 'score-guide-strip');
    strip.setAttribute('aria-hidden', 'true');
    for (const [index, value] of values.entries()) {
      const tile = ctx.e('span', 'score-guide-tile', value);
      tile.style.setProperty('--practice-colour', `var(--game-colour-${index % 4})`);
      strip.append(tile);
    }
    const practice = ctx.e('button', 'game-action score-guide-practice');
    practice.type = 'button';
    paintIconAction(ctx, practice, profile.icon, profile.steps[step][3], profile.steps[step][3]);
    practice.disabled = practiced;
    practice.addEventListener('click', () => {
      if (disposed || panel.hidden || practiced) return;
      practiced = true;
      demo.dataset.practiced = 'true';
      paintDemo(profile.steps[step][4]);
      result.textContent = 'Got it! Your game and score are waiting.';
      next.focus?.({ preventScroll: true });
    });
    demo.append(strip, practice);
  }
  function paint() {
    practiced = false;
    delete demo.dataset.practiced;
    title.textContent = profile.steps[step][0];
    count.textContent = `${step + 1} / 3`;
    caption.textContent = profile.steps[step][1];
    result.textContent = 'Try this example · No game points or moves used';
    paintDemo(profile.steps[step][2]);
    paintIconAction(
      ctx,
      next,
      step === 2 ? 'play' : 'arrow-right',
      step === 2 ? 'Play now' : 'Next introduction step',
      step === 2 ? 'Play' : 'Next',
    );
  }
  function cancel() {
    panel.hidden = true;
    board.inert = false;
  }
  function close() {
    cancel();
    try {
      window.sessionStorage?.setItem(storageKey, 'seen');
    } catch {
      /* Optional tab memory never blocks play. */
    }
    ([...board.children].find((cell) => !cell.disabled) || board || help).focus?.({
      preventScroll: true,
    });
  }
  function show(focus = true) {
    if (disposed) return;
    step = 0;
    paint();
    panel.hidden = false;
    board.inert = true;
    if (focus) skip.focus?.({ preventScroll: true });
  }
  skip.addEventListener('click', close);
  next.addEventListener('click', () => {
    if (step === 2) close();
    else {
      step++;
      paint();
    }
  });
  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    close();
  });
  let seen = false;
  try {
    seen = window.sessionStorage?.getItem(storageKey) === 'seen';
  } catch {
    /* Help remains available without browser storage. */
  }
  if (auto && !seen) show(false);
  return {
    show,
    cancel,
    isOpen: () => !disposed && !panel.hidden,
    dispose() {
      disposed = true;
      cancel();
      panel.remove();
    },
  };
}
